// eslint-disable-next-line deprecate/import
import Link from 'next/link';

import type { ComponentPropsWithoutRef, MouseEvent} from 'react';
import { useCallback } from 'react';

import classNames from 'classnames';
import { useDisconnect } from 'wagmi';

// import { useDisconnect } from 'wagmi';
import { Dropdown, DropdownItem } from '@uikit/components/Dropdown';
import { AngleRight } from '@uikit/icons/AngleRight';

import { useAuth } from '@shared/common/providers/AuthProvider';
import { useTagManager } from '@shared/common/providers/GoogleTagManagerProvider';
import truncate from '@shared/helpers/web3/truncate';
import useLoginWithGivenWallet from '@shared/hooks/useLoginWithGivenWallet';
import { useRegisterQueryParams } from '@shared/hooks/useRegisterQueryParams';

interface ConnectWalletButtonProps extends ComponentPropsWithoutRef<'button'> {
  showDisconnectPrefix?: boolean;
}

export const useAuthModal = () => {
  const { logout } = useAuth();
  const { mutateAsync: loginWithWallet, isPending: isLoading } = useLoginWithGivenWallet({
    onLogout: logout,
  });
  const addQueryRegister = useRegisterQueryParams();
  const { triggerAuthEvent } = useTagManager();

  const login = useCallback(async () => {
    const userInfo = await loginWithWallet({
      signProps: {
        walletType: 'WalletConnect2',
        signType: 'SIWE',
        message: 'Confirm authorization in Magic Launchpad Platform with your wallet',
        shouldVerifyChain: true,
        shouldReconnect: true,
      },
    });

    if (!userInfo) {
      return;
    }

    if (userInfo.isNewUser) {
      triggerAuthEvent(userInfo.user.id, {
        isNewUser: true,
      });
      addQueryRegister({
        loginMethod: 'WalletConnect2',
      });
    }
  }, [triggerAuthEvent, loginWithWallet, addQueryRegister]);

  return {
    login,
    isLoading,
  };
};

const ConnectWalletButton = ({
  className,
  showDisconnectPrefix,
  ...props
}: ConnectWalletButtonProps) => {
  const { isAuth, isLoading, logout, userEvmWallet } = useAuth();
  const { login: loginWithWallet, isLoading: isLoggingInWithWallet } = useAuthModal();
  const { disconnect } = useDisconnect();

  const handleDisconnect = () => {
    logout();
    disconnect();
  };

  const isConnectWalletButtonDisabled = isLoading || isLoggingInWithWallet;

  if (!userEvmWallet?.attributes?.address || !isAuth) {
    return (
      <button
        disabled={isConnectWalletButtonDisabled}
        onClick={loginWithWallet}
        data-qa="connect-wallet-button"
        className={classNames('ui-button ui-button-solid-primary min-w-32 px-4', className)}
        {...props}
      >
        {!isAuth ? 'Connect Wallet' : 'No EVM Wallet'}
      </button>
    );
  }

  return (
    <Dropdown
      trigger={({ triggerProps: { onClick, ...triggerProps } }) => {
        const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
          if (onClick) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            //@ts-ignore
            onClick(event);
          }
        };

        return (
          <button
            disabled={isLoading}
            onClick={handleClick}
            data-qa="connected-wallet-button"
            {...triggerProps}
            className={classNames(
              'ui-button ui-button-solid-primary min-w-32 gap-2 px-4',
              className,
            )}
            {...props}
          >
            {showDisconnectPrefix && 'Disconnect '}
            {truncate(userEvmWallet.attributes.address)}
            <AngleRight className="icon-xs rotate-90" />
          </button>
        );
      }}
    >
      <DropdownItem Component={Link} href="/portfolio" label="Dashboard" />
      <DropdownItem onClick={handleDisconnect} label="Disconnect" />
    </Dropdown>
  );
};

export default ConnectWalletButton;
